import React, { useState } from 'react';
import { Modal, Button, Form } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { isAfter, isValid, parse } from 'date-fns';
import { getOperatorOptionsForColumn, timeOperatorOptions } from './constants';
import { FilterRow } from './components/FilterRow';

const createInitialRow = () => ({
  id: Math.random(),
  column: '',
  operator: 'is',
  value: '',
  multiValues: [],
});

const mapFilterToRow = (f) => {
  const isTime = f.column === 'time';
  const defaultOp = isTime ? 'is after' : 'is';
  const operator = f.operator || defaultOp;
  const isMultiValue = operator === 'is any of';
  return {
    id: Math.random(),
    column: f.column || '',
    operator,
    value: !isMultiValue ? (f.value || '') : '',
    multiValues: isMultiValue ? f.value : [],
  };
};

const getUsedTimeOperators = (rows, rowId) => new Set(
  rows
    .filter(row => row.id !== rowId && row.column === 'time')
    .map(row => row.operator),
);

const getColumnOptionsForRow = (row, rows, options) => {
  if (row.column === 'time') return options;

  const usedTimeOperators = getUsedTimeOperators(rows, row.id);
  const hasAvailableTimeOperator = timeOperatorOptions.some(option => !usedTimeOperators.has(option.value));

  if (hasAvailableTimeOperator) return options;
  return options.filter(option => option.value !== 'time');
};

const getOperatorOptionsForRow = (row, rows) => {
  if (row.column !== 'time') {
    return getOperatorOptionsForColumn(row.column);
  }

  const usedTimeOperators = getUsedTimeOperators(rows, row.id);
  return timeOperatorOptions.filter(option => !usedTimeOperators.has(option.value) || option.value === row.operator);
};

const getDefaultTimeOperator = (rows, rowId) => {
  const usedTimeOperators = getUsedTimeOperators(rows, rowId);
  const availableOperator = timeOperatorOptions.find(option => !usedTimeOperators.has(option.value));
  return availableOperator ? availableOperator.value : 'is after';
};

const TIME_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

const isFutureTimeValue = (value) => {
  if (!value) return false;

  const parsedValue = parse(value, TIME_INPUT_FORMAT, new Date());
  return isValid(parsedValue) && isAfter(parsedValue, new Date());
};

const getInitialFilterRows = (currentFilter, dropdownColumns) => {
  if (Array.isArray(currentFilter) && currentFilter.length > 0) {
    return currentFilter.map(f => mapFilterToRow(f, dropdownColumns));
  }
  if (currentFilter && typeof currentFilter === 'object' && currentFilter.column) {
    return [mapFilterToRow(currentFilter, dropdownColumns)];
  }
  return [createInitialRow()];
};

export const FilterModal = ({
  isOpen,
  onClose,
  currentFilter,
  onApply,
  columnOptions,
  dropdownOptionsMap = {},
  dropdownColumns = [],
  headerTitle = 'Filter Items',
}) => {

  const intl = useIntl();
  const timeValidationMessage = intl.formatMessage({
    id: 'timeMustBeInPast',
    defaultMessage: 'Please choose a time in the past',
  });

  const [filterRows, setFilterRows] = useState(() => getInitialFilterRows(currentFilter, dropdownColumns));

  const handleAddRow = () => {
    setFilterRows(prev => [...prev, createInitialRow()]);
  };

  const handleRemoveRow = (id) => {
    setFilterRows(prev => prev.filter(row => row.id !== id));
  };

  const handleRowChange = (id, fields) => {
    setFilterRows(prev => prev.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, ...fields };
        if ('column' in fields) {
          updatedRow.value = '';
          updatedRow.multiValues = [];
          if (fields.column === 'time') {
            updatedRow.operator = getDefaultTimeOperator(prev, id);
          } else if (fields.column === 'user' || fields.column === 'zone') {
            updatedRow.operator = 'is any of';
          } else {
            updatedRow.operator = 'is';
          }
        }
        return updatedRow;
      }
      return row;
    }));
  };

  const handleApply = () => {
    if (filterRows.some(row => row.column === 'time' && isFutureTimeValue(row.value))) {
      return;
    }

    const validFilters = filterRows
      .filter(row => row.column)
      .map(row => {
        const isMulti = row.operator === 'is any of';
        return {
          column: row.column,
          operator: row.operator,
          value: isMulti ? row.multiValues : row.value,
        };
      });

    onApply(validFilters);
    onClose();
  };

  const isApplyDisabled = !filterRows.some(row => row.column)
    || filterRows.some(row => {
      if (!row.column) return false;
      const isMulti = row.operator === 'is any of';
      if (row.column === 'time' && isFutureTimeValue(row.value)) return true;
      return isMulti
        ? !row.multiValues || row.multiValues.length === 0
        : !row.value || !row.value.trim();
    });

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="large"
      centered
      dimmer="inverted"
    >
      <Modal.Header>{headerTitle}</Modal.Header>
      <Modal.Content>
        <Form onSubmit={(e) => { e.preventDefault(); handleApply(); }}>
          {filterRows.map((row) => (
            <FilterRow
              key={row.id}
              row={row}
              columnOptions={getColumnOptionsForRow(row, filterRows, columnOptions)}
              operatorOptions={getOperatorOptionsForRow(row, filterRows)}
              dropdownColumns={dropdownColumns}
              dropdownOptionsMap={dropdownOptionsMap}
              onRowChange={handleRowChange}
              onRemoveRow={handleRemoveRow}
              isRemoveDisabled={filterRows.length === 1}
              hasTimeValidationError={row.column === 'time' && isFutureTimeValue(row.value)}
              timeValidationMessage={timeValidationMessage}
            />
          ))}

          <Button
            type="button"
            content={intl.formatMessage({
              id: 'addFilter',
              defaultMessage: '+ add filter',
            })}
            onClick={handleAddRow}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          color="red"
          basic
          type="button"
          onClick={() => {
            onApply([]);
            onClose();
          }}
          style={{ float: 'left' }}
        >
          {intl.formatMessage({
            id: 'clearFilters',
            defaultMessage: 'Clear Filters',
          })}
        </Button>
        <Button onClick={onClose}>
          {intl.formatMessage({
            id: 'cancel',
            defaultMessage: 'Cancel',
          })}
        </Button>
        <Button
          primary
          onClick={handleApply}
          disabled={isApplyDisabled}
        >
          {intl.formatMessage({
            id: 'apply',
            defaultMessage: 'Apply',
          })}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
